import { api } from './api';
import { IChatMessage } from '@/types/chat-types';
type CleanupFunction = () => void;

export interface ChatService {
  sendMessage: (
    message: string,
    userInput: any,
    playbookId: string,
    sessionId: string,
    messages: IChatMessage[],
    onData: (data: string, isEvent: boolean, eventType?: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    setUserInput: (input: any) => void,
    signal?: AbortSignal
  ) => Promise<CleanupFunction>;
}

function isAbortError(error: unknown): error is DOMException {
  return (
    error instanceof DOMException &&
    (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

// Helper functions to reduce complexity
const createAbortController = (signal?: AbortSignal): { controller: AbortController; cleanup: () => void } => {
  const controller = new AbortController();
  
  const cleanup = (): void => {
    controller.abort();
    if (signal) {
      signal.removeEventListener('abort', handleAbort);
    }
  };

  const handleAbort = (): void => {
    controller.abort();
  };

  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', handleAbort, { once: true });
    }
  }

  return { controller, cleanup };
};

const buildRequestBody = (message: string, userInput: any, playbookId: string, sessionId: string, messages: IChatMessage[]) => {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  
  // Create the base slot_value object
  const slotValue: any = {
    type: userInput?.data?.type,
    key: userInput?.data?.text,
    value: message
  };

  // Add url property if it's a file upload
  if (userInput?.data?.type === 'FILE_UPLOAD' && userInput?.data?.url) {
    slotValue.url = userInput.data.url;
  }

  const baseBody = {
    playbook_id: playbookId,
    session_id: sessionId,
    user: userData?.email,
    chat_messages: messages,
    user_input: {
      slot_id: userInput?.data?.input_id,
      slot_value: slotValue
    },
  };

  return baseBody;
};

const makeRequest = async (requestBody: any, signal?: AbortSignal) => {
  const response = await fetch(`${process.env.REACT_APP_AUTOGEN_BASE_URL || 'https://genie-autogen.azurewebsites.net'}/execute-playbook`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  return response;
};

const processToolCallEvent = (message: any, onData: (data: any, isEvent: boolean, eventType?: string) => void) => {
  if (message.type !== 'ToolCallExecutionEvent' || message.source !== "user_interaction_agent") {
    return;
  }

  const content = message.content;
  for (let i = 0; i < content.length; i++) {
    let argument = content[i].content;
    argument = argument.replace(/'/g, '"')            
      .replace(/\bTrue\b/g, 'true')      
      .replace(/\bFalse\b/g, 'false');   
    
    if (typeof argument === 'string') {    
      try {
        argument = JSON.parse(argument);
      } catch (e) {
        return;
      }
    }

    if (argument["type"] === "UserMessage") {
      onData(argument["user_message"], true, 'text');
    } else {
      onData(argument?.data?.text, true, 'text');
      onData(argument, true, 'tool');
    }
  }
};

const processEvent = (event: any, onData: (data: any, isEvent: boolean, eventType?: string) => void) => {
  try {
    let message = event.message;
    if (typeof message === 'string') {
      message = JSON.parse(message);
    }
    
    processToolCallEvent(message, onData);
  } catch (e) {
    // Silently ignore processing errors
  }
};

const processSSEMessage = (message: string, onData: (data: any, isEvent: boolean, eventType?: string) => void, onComplete: () => void) => {
  if (message === '[DONE]') {
    onComplete();
    return;
  }

  try {
    const event = JSON.parse(message);
    processEvent(event, onData);
  } catch (e) {
    // Silently ignore parsing errors
  }
};

const processStreamBuffer = (buffer: string, onData: (data: any, isEvent: boolean, eventType?: string) => void, onComplete: () => void) => {
  let remainingBuffer = buffer;
  
  while (true) {
    const match = /^data: ([\s\S]*?)(?:\n\n|$)/.exec(remainingBuffer);
    if (!match) break;

    const message = match[1].trim();
    remainingBuffer = remainingBuffer.slice(match[0].length);

    processSSEMessage(message, onData, onComplete);
  }
  
  return remainingBuffer;
};

const processStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onData: (data: any, isEvent: boolean, eventType?: string) => void,
  onComplete: () => void,
  onError: (error: Error) => void
): Promise<void> => {
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) {
          try {
            const event = JSON.parse(buffer);
            processEvent(event, onData);
          } catch (e) {
            // Silently ignore parsing errors
          }
        }
        onComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = processStreamBuffer(buffer, onData, onComplete);
    }
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      onError(error);
    }
  } finally {
    reader.releaseLock();
  }
};

export const chatService: ChatService = {
  async sendMessage(
    message: string,
    userInput: any,
    playbookId: string,
    sessionId: string,
    messages: IChatMessage[],
    onData: (data: any, isEvent: boolean, eventType?: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    setUserInput: (input: any) => void,
    signal?: AbortSignal
  ): Promise<CleanupFunction> {
    const { controller, cleanup } = createAbortController(signal);

    try {
      const requestBody = buildRequestBody(message, userInput, playbookId, sessionId, messages);
      const response = await makeRequest(requestBody, signal);
      
      setUserInput(null);
      
      const reader = response.body!.getReader();
      
      processStream(reader, onData, onComplete, onError).catch((error) => {
        if (!isAbortError(error)) {
          onError(error instanceof Error ? error : new Error('Error processing stream'));
        }
      });

      return cleanup;
    } catch (error) {
      cleanup();

      if (!isAbortError(error)) {
        onError(error instanceof Error ? error : new Error('An unknown error occurred'));
      }

      return () => { }; // No cleanup needed as we already cleaned up
    }
  },
};
